import React, { useState, useEffect } from 'react';
import {
  Play, Users, Trophy, ChevronRight, CheckCircle,
  X, AlertTriangle, Zap, Crown, Swords, Clock, RotateCcw
} from 'lucide-react';
import { supabase } from '../lib/supabase';

const FALLBACK_RECORDING_URL = 'https://fvaaxuhjofhbngkalqcv.supabase.co/storage/v1/object/public/recordings/17ad4147-41e3-4b2c-980f-1b1f209a4f9f/1771077791840-video-recording.mp4';
const FALLBACK_VOCALS_URL = 'https://fvaaxuhjofhbngkalqcv.supabase.co/storage/v1/object/public/recordings/17ad4147-41e3-4b2c-980f-1b1f209a4f9f/849a047e-d013-4d5a-a123-ae95be63812e_vocals_only.mp3';

interface SimParticipant {
  userId: string;
  username: string;
  entryId: string;
  seed: number;
  currentRound: number;
  eliminated: boolean;
  scores: Record<number, number>;
}

interface SimMatch {
  id: string;
  round: number;
  matchNumber: number;
  p1?: SimParticipant;
  p2?: SimParticipant;
  winner?: SimParticipant;
  status: 'pending' | 'ready' | 'simulated';
  nextMatchId?: string;
}

interface SimState {
  participants: SimParticipant[];
  matches: SimMatch[];
  totalRounds: number;
  currentRound: number;
  seeded: boolean;
}

interface UserSearchResult {
  id: string;
  username: string;
  full_name: string | null;
  email?: string;
}

interface Props {
  tournamentId: string;
  tournamentTitle: string;
  tournamentSongs: Array<{ id: string; song_id: string; song?: { id: string; title: string; artist: string } }>;
  maxParticipants: number;
  onClose: () => void;
}

export const SimulateTournament: React.FC<Props> = ({
  tournamentId,
  tournamentTitle,
  tournamentSongs,
  maxParticipants,
  onClose
}) => {
  const [simState, setSimState] = useState<SimState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [simulatingMatchId, setSimulatingMatchId] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<Record<string, string>>({});
  const [selectedWinners, setSelectedWinners] = useState<Record<string, string>>({});

  const [selectedUsers, setSelectedUsers] = useState<UserSearchResult[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    loadProfilesThenCheckSimulation();
  }, [tournamentId]);

  const loadProfilesThenCheckSimulation = async () => {
    const profileMap = await loadProfiles();
    await checkExistingSimulation(profileMap);
  };

  const loadProfiles = async (): Promise<Record<string, string>> => {
    const { data } = await supabase
      .from('profiles')
      .select('id, username, full_name')
      .limit(200);
    const map: Record<string, string> = {};
    if (data) {
      data.forEach(p => { map[p.id] = p.username || p.full_name || ''; });
      setProfiles(map);
    }
    return map;
  };

  const searchUsers = async (q: string) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const { data } = await supabase.rpc('search_users', { query: q });
      setSearchResults((data || []).filter((u: UserSearchResult) => !selectedUsers.find(s => s.id === u.id)));
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => searchUsers(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery, selectedUsers]);

  const checkExistingSimulation = async (profileMap?: Record<string, string>) => {
    const resolvedProfiles = profileMap ?? profiles;
    const { data: entries } = await supabase
      .from('tournament_entries')
      .select('id, user_id, bracket_seed, current_round, eliminated_at, score, status')
      .eq('tournament_id', tournamentId);

    if (!entries || entries.length === 0) return;

    const missingUserIds = entries
      .filter(e => !resolvedProfiles[e.user_id])
      .map(e => e.user_id);

    let finalProfileMap = resolvedProfiles;
    if (missingUserIds.length > 0) {
      const { data: extraProfiles } = await supabase
        .from('profiles')
        .select('id, username, full_name')
        .in('id', missingUserIds);
      if (extraProfiles && extraProfiles.length > 0) {
        const extra: Record<string, string> = {};
        extraProfiles.forEach(p => { extra[p.id] = p.username || p.full_name || ''; });
        finalProfileMap = { ...resolvedProfiles, ...extra };
        setProfiles(prev => ({ ...prev, ...extra }));
      }
    }

    const stillMissing = entries.filter(e => !finalProfileMap[e.user_id]);
    if (stillMissing.length > 0) {
      setError(`Could not load display names for ${stillMissing.length} participant(s). Please reset and try again.`);
      return;
    }

    const { data: matches } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', tournamentId)
      .order('round_number')
      .order('match_number');

    const { data: bracket } = await supabase
      .from('bracket_progression')
      .select('*')
      .eq('tournament_id', tournamentId)
      .maybeSingle();

    if (!bracket || !matches) return;

    const participants = buildParticipantsFromEntries(entries, finalProfileMap);
    const simMatches = buildSimMatchesFromDB(matches, participants);

    setSimState({
      participants,
      matches: simMatches,
      totalRounds: bracket.total_rounds,
      currentRound: getCurrentRound(simMatches),
      seeded: true
    });
  };

  const buildParticipantsFromEntries = (entries: any[], profileMap: Record<string, string> = profiles): SimParticipant[] => {
    return entries.map(e => ({
      userId: e.user_id,
      username: profileMap[e.user_id] || '',
      entryId: e.id,
      seed: e.bracket_seed || 1,
      currentRound: e.current_round || 1,
      eliminated: !!e.eliminated_at,
      scores: {}
    }));
  };

  const buildSimMatchesFromDB = (matches: any[], participants: SimParticipant[]): SimMatch[] => {
    const entryMap: Record<string, SimParticipant> = {};
    participants.forEach(p => { entryMap[p.entryId] = p; });

    return matches.map(m => {
      const p1 = m.participant1_entry_id ? entryMap[m.participant1_entry_id] : undefined;
      const p2 = m.participant2_entry_id ? entryMap[m.participant2_entry_id] : undefined;
      const winner = m.winner_id ? participants.find(p => p.userId === m.winner_id) : undefined;
      return {
        id: m.id,
        round: m.round_number,
        matchNumber: m.match_number,
        p1,
        p2,
        winner,
        status: m.match_status === 'completed' ? 'simulated' : (p1 && p2 ? 'ready' : 'pending'),
        nextMatchId: m.next_match_id
      };
    });
  };

  const getCurrentRound = (matches: SimMatch[]): number => {
    const pendingRounds = matches.filter(m => m.status !== 'simulated' && m.p1 && m.p2).map(m => m.round);
    if (pendingRounds.length === 0) return 3;
    return Math.min(...pendingRounds);
  };

  const handleSeedTournament = async () => {
    if (selectedUsers.length < 2) {
      setError('Add at least 2 participants to start the simulation.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      await cleanupSimulation();

      const slotsToFill = maxParticipants - selectedUsers.length;
      let allUsers = [...selectedUsers];

      if (slotsToFill > 0) {
        const selectedIds = selectedUsers.map(u => u.id);
        const { data: randomPool } = await supabase
          .from('profiles')
          .select('id, username, full_name')
          .not('id', 'in', `(${selectedIds.join(',')})`)
          .limit(slotsToFill * 5);

        if (randomPool && randomPool.length > 0) {
          const shuffled = [...randomPool].sort(() => Math.random() - 0.5);
          const picked = shuffled.slice(0, slotsToFill);
          allUsers = [...selectedUsers, ...picked];
        }
      }

      const count = allUsers.length;
      const totalRounds = Math.ceil(Math.log2(count));
      const bracketSize = Math.pow(2, totalRounds);
      const seededUserIds = allUsers.map(u => u.id);

      const firstSong = tournamentSongs[0];
      if (!firstSong) throw new Error('Tournament has no songs. Add at least one song first.');

      const entries: any[] = [];
      for (let i = 0; i < count; i++) {
        const { data: entry, error: entryErr } = await supabase
          .from('tournament_entries')
          .insert({
            tournament_id: tournamentId,
            user_id: seededUserIds[i],
            status: 'registered',
            bracket_seed: i + 1,
            current_round: 1
          })
          .select()
          .single();
        if (entryErr) throw entryErr;
        entries.push(entry);
      }

      const matchesByRound: any[][] = [];
      for (let r = 1; r <= totalRounds; r++) {
        const roundMatchCount = Math.pow(2, totalRounds - r);
        const roundMatches: any[] = [];
        for (let m = 0; m < roundMatchCount; m++) {
          const isFirstRound = r === 1;
          const p1Idx = isFirstRound ? m * 2 : undefined;
          const p2Idx = isFirstRound ? m * 2 + 1 : undefined;
          const insertData: any = {
            tournament_id: tournamentId,
            round_number: r,
            match_number: m + 1,
            match_status: 'pending'
          };
          if (isFirstRound && p1Idx !== undefined && p1Idx < count) {
            insertData.participant1_id = seededUserIds[p1Idx];
            insertData.participant1_entry_id = entries[p1Idx].id;
          }
          if (isFirstRound && p2Idx !== undefined && p2Idx < count) {
            insertData.participant2_id = seededUserIds[p2Idx];
            insertData.participant2_entry_id = entries[p2Idx].id;
          }
          const { data: match, error: matchErr } = await supabase
            .from('tournament_matches')
            .insert(insertData)
            .select()
            .single();
          if (matchErr) throw matchErr;
          roundMatches.push(match);
        }
        matchesByRound.push(roundMatches);
      }

      for (let r = 0; r < totalRounds - 1; r++) {
        for (let m = 0; m < matchesByRound[r].length; m++) {
          const nextMatchIdx = Math.floor(m / 2);
          await supabase.from('tournament_matches')
            .update({ next_match_id: matchesByRound[r + 1][nextMatchIdx].id })
            .eq('id', matchesByRound[r][m].id);
        }
      }

      await supabase.from('bracket_progression').upsert({
        tournament_id: tournamentId,
        total_rounds: totalRounds,
        bracket_size: bracketSize,
        bracket_type: 'single_elimination',
        seeding_method: 'random'
      }, { onConflict: 'tournament_id' });

      await supabase.from('tournaments').update({ registration_locked: true, locked_participant_count: count }).eq('id', tournamentId);

      const participants: SimParticipant[] = allUsers.map((u, i) => ({
        userId: u.id,
        username: u.username || u.full_name || '',
        entryId: entries[i].id,
        seed: i + 1,
        currentRound: 1,
        eliminated: false,
        scores: {}
      }));

      const allMatches: SimMatch[] = matchesByRound.flatMap((roundMatches, ri) =>
        roundMatches.map((m, mi) => {
          const p1 = ri === 0 ? participants[mi * 2] : undefined;
          const p2 = ri === 0 && mi * 2 + 1 < count ? participants[mi * 2 + 1] : undefined;
          const nextRoundMatches = matchesByRound[ri + 1];
          return {
            id: m.id,
            round: ri + 1,
            matchNumber: mi + 1,
            p1,
            p2,
            status: (p1 && p2 ? 'ready' : 'pending') as SimMatch['status'],
            nextMatchId: nextRoundMatches?.[Math.floor(mi / 2)]?.id
          };
        })
      );

      setSimState({
        participants,
        matches: allMatches,
        totalRounds,
        currentRound: 1,
        seeded: true
      });

      const manualCount = selectedUsers.length;
      const randomCount = count - manualCount;
      const fillNote = randomCount > 0 ? ` (${manualCount} manual + ${randomCount} random)` : '';
      setSuccess(`${count} participants seeded${fillNote}. Round 1 is ready to simulate.`);
    } catch (err: any) {
      console.error('Seeding error:', err);
      setError(err.message || 'Failed to seed tournament');
    } finally {
      setLoading(false);
    }
  };

  const cleanupSimulation = async () => {
    await supabase.from('tournament_matches').delete().eq('tournament_id', tournamentId);
    await supabase.from('bracket_progression').delete().eq('tournament_id', tournamentId);
    const { data: existingEntries } = await supabase.from('tournament_entries').select('id, performance_id').eq('tournament_id', tournamentId);
    if (existingEntries && existingEntries.length > 0) {
      const performanceIds = existingEntries.map(e => e.performance_id).filter(Boolean);
      if (performanceIds.length > 0) {
        const { data: perfRecordings } = await supabase.from('performances').select('recording_id').in('id', performanceIds);
        if (perfRecordings) {
          const recIds = perfRecordings.map(p => p.recording_id).filter(Boolean);
          if (recIds.length > 0) {
            await supabase.from('recordings').delete().in('id', recIds);
          }
        }
        await supabase.from('performances').delete().in('id', performanceIds);
      }
      await supabase.from('tournament_entries').delete().eq('tournament_id', tournamentId);
    }
    await supabase.from('recordings').delete().eq('tournament_id', tournamentId);
    await supabase.from('tournaments').update({ registration_locked: false, locked_participant_count: null, locked_at: null }).eq('id', tournamentId);
  };

  const handleReset = async () => {
    setLoading(true);
    setError(null);
    try {
      await cleanupSimulation();
      setSimState(null);
      setSuccess('Simulation reset. Ready to seed again.');
    } catch (err: any) {
      setError(err.message || 'Failed to reset');
    } finally {
      setLoading(false);
    }
  };

  const getRandomSong = () => {
    if (!tournamentSongs.length) return null;
    return tournamentSongs[Math.floor(Math.random() * tournamentSongs.length)];
  };

  const handleSimulateMatch = async (match: SimMatch) => {
    if (!match.p1 || !match.p2 || !simState) return;
    setSimulatingMatchId(match.id);
    setError(null);

    try {
      const pickedWinnerId = selectedWinners[match.id];

      let score1: number;
      let score2: number;

      if (pickedWinnerId) {
        const winnerIsP1 = pickedWinnerId === match.p1.userId;
        const winnerScore = Math.floor(Math.random() * 20) + 75;
        const loserScore = Math.floor(Math.random() * (winnerScore - 60)) + 60;
        score1 = winnerIsP1 ? winnerScore : loserScore;
        score2 = winnerIsP1 ? loserScore : winnerScore;
      } else {
        score1 = Math.floor(Math.random() * 30) + 60;
        score2 = Math.floor(Math.random() * 30) + 60;
      }

      const winner = score1 >= score2 ? match.p1 : match.p2;
      const loser = score1 >= score2 ? match.p2 : match.p1;

      const song1 = getRandomSong();
      const song2 = getRandomSong();

      const rec1 = await createSimRecording(match.p1.userId, song1?.song_id || song1?.id || '', match.round, tournamentId);
      const rec2 = await createSimRecording(match.p2.userId, song2?.song_id || song2?.id || '', match.round, tournamentId);

      const perf1 = await createSimPerformance(match.p1.userId, song1?.song_id || song1?.id || '', rec1.id, rec1.recording_url, score1);
      const perf2 = await createSimPerformance(match.p2.userId, song2?.song_id || song2?.id || '', rec2.id, rec2.recording_url, score2);

      await supabase.from('tournament_entries').update({
        performance_id: perf1.id,
        status: 'evaluated',
        score: score1
      }).eq('id', match.p1!.entryId);

      await supabase.from('tournament_entries').update({
        performance_id: perf2.id,
        status: 'evaluated',
        score: score2
      }).eq('id', match.p2!.entryId);

      await supabase.from('tournament_entries').update({
        eliminated_at: new Date().toISOString()
      }).eq('id', loser.entryId);

      await supabase.from('tournament_matches').update({
        match_status: 'completed',
        winner_id: winner.userId,
        participant1_recording_id: rec1.id,
        participant2_recording_id: rec2.id,
        completed_at: new Date().toISOString()
      }).eq('id', match.id);

      const updatedParticipants = simState.participants.map(p => {
        if (p.entryId === winner.entryId) {
          return { ...p, scores: { ...p.scores, [match.round]: score1 >= score2 ? score1 : score2 }, currentRound: match.round + 1 };
        }
        if (p.entryId === loser.entryId) {
          return { ...p, scores: { ...p.scores, [match.round]: score1 >= score2 ? score2 : score1 }, eliminated: true };
        }
        return p;
      });

      if (match.nextMatchId) {
        const fillsSlot1 = (match.matchNumber % 2) === 1;
        if (fillsSlot1) {
          await supabase.from('tournament_matches').update({
            participant1_id: winner.userId,
            participant1_entry_id: winner.entryId
          }).eq('id', match.nextMatchId);
        } else {
          await supabase.from('tournament_matches').update({
            participant2_id: winner.userId,
            participant2_entry_id: winner.entryId
          }).eq('id', match.nextMatchId);
        }

        await supabase.from('tournament_entries').update({ current_round: match.round + 1 }).eq('id', winner.entryId);
      }

      const updatedMatches = await refreshMatchesFromDB(simState.matches, updatedParticipants);

      const nextRound = getNextActiveRound(updatedMatches);
      setSimState({
        ...simState,
        participants: updatedParticipants,
        matches: updatedMatches,
        currentRound: nextRound
      });

      setSelectedWinners(prev => { const next = { ...prev }; delete next[match.id]; return next; });
      setSuccess(`Match simulated! ${winner.username} wins (${score1 >= score2 ? score1 : score2} vs ${score1 >= score2 ? score2 : score1})`);
    } catch (err: any) {
      console.error('Match simulation error:', err);
      setError(err.message || 'Failed to simulate match');
    } finally {
      setSimulatingMatchId(null);
    }
  };

  const refreshMatchesFromDB = async (currentMatches: SimMatch[], participants: SimParticipant[]): Promise<SimMatch[]> => {
    const { data: dbMatches } = await supabase
      .from('tournament_matches')
      .select('*')
      .eq('tournament_id', tournamentId)
      .order('round_number')
      .order('match_number');

    if (!dbMatches) return currentMatches;

    const entryMap: Record<string, SimParticipant> = {};
    participants.forEach(p => { entryMap[p.entryId] = p; });
    const userMap: Record<string, SimParticipant> = {};
    participants.forEach(p => { userMap[p.userId] = p; });

    return dbMatches.map(m => {
      const p1 = m.participant1_entry_id ? entryMap[m.participant1_entry_id] : undefined;
      const p2 = m.participant2_entry_id ? entryMap[m.participant2_entry_id] : undefined;
      const winner = m.winner_id ? userMap[m.winner_id] : undefined;
      return {
        id: m.id,
        round: m.round_number,
        matchNumber: m.match_number,
        p1,
        p2,
        winner,
        status: m.match_status === 'completed' ? 'simulated' : (p1 && p2 ? 'ready' : 'pending'),
        nextMatchId: m.next_match_id
      };
    });
  };

  const getNextActiveRound = (matches: SimMatch[]): number => {
    const ready = matches.filter(m => m.status === 'ready');
    if (ready.length === 0) return simState?.totalRounds || 3;
    return Math.min(...ready.map(m => m.round));
  };

  const createSimRecording = async (userId: string, songId: string, round: number, tournamentId: string) => {
    const { data, error } = await supabase
      .from('recordings')
      .insert({
        user_id: userId,
        song_id: songId,
        recording_url: FALLBACK_RECORDING_URL,
        recording_type: 'video',
        vocals_only_url: FALLBACK_VOCALS_URL,
        processing_status: 'completed',
        video_processing_status: 'completed',
        preprocessing_status: 'completed',
        tournament_id: tournamentId,
        tournament_round: round,
        is_tournament_submission: false,
        submitted_at: new Date().toISOString(),
        leaderboard_status: 'unsubmitted'
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  };

  const createSimPerformance = async (userId: string, songId: string, recordingId: string, vocalUrl: string, score: number) => {
    const { data, error } = await supabase
      .from('performances')
      .insert({
        user_id: userId,
        song_id: songId,
        recording_id: recordingId,
        vocal_recording_url: vocalUrl,
        score
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  };

  const getRoundLabel = (round: number, total: number) => {
    if (round === total) return 'Final';
    if (round === total - 1) return 'Semi-Finals';
    return `Round ${round}`;
  };

  const getStatusColor = (p: SimParticipant) => {
    if (p.eliminated) return 'text-red-400';
    return 'text-white';
  };

  const roundGroups = simState?.matches.reduce((acc, m) => {
    if (!acc[m.round]) acc[m.round] = [];
    acc[m.round].push(m);
    return acc;
  }, {} as Record<number, SimMatch[]>) || {};

  return (
    <div className="fixed inset-0 bg-black/90 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-kod-purple-800 rounded-xl max-w-5xl w-full my-8 border border-kod-purple-600">
        <div className="p-6 border-b border-kod-purple-600 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-orange-500/20 p-2 rounded-lg">
              <Zap size={20} className="text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Tournament Simulator</h2>
              <p className="text-white/60 text-sm">{tournamentTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4 flex items-center gap-3 text-red-400">
              <AlertTriangle size={18} />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto"><X size={16} /></button>
            </div>
          )}
          {success && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-4 flex items-center gap-3 text-green-400">
              <CheckCircle size={18} />
              <span>{success}</span>
              <button onClick={() => setSuccess(null)} className="ml-auto"><X size={16} /></button>
            </div>
          )}

          {!simState?.seeded ? (
            <div className="max-w-xl mx-auto py-6 space-y-5">
              <div className="text-center">
                <div className="bg-kod-purple-700/50 rounded-full inline-flex p-5 mb-4">
                  <Users size={36} className="text-white/40" />
                </div>
                <h3 className="text-xl font-bold mb-1">Select Participants</h3>
                <p className="text-white/50 text-sm">
                  Search and add up to {maxParticipants} participants. Any unfilled slots will be randomly assigned from existing users.
                </p>
              </div>

              <div className="relative">
                <div className="flex items-center gap-2 bg-kod-purple-700/60 border border-kod-purple-500/50 rounded-lg px-3 py-2.5">
                  <svg className="w-4 h-4 text-white/40 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search by username, name or email…"
                    className="bg-transparent flex-1 text-sm text-white placeholder-white/30 outline-none"
                  />
                  {searchLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/40 flex-shrink-0" />
                  )}
                </div>

                {searchResults.length > 0 && (
                  <div className="absolute z-10 top-full mt-1 w-full bg-kod-purple-800 border border-kod-purple-500/60 rounded-lg shadow-xl overflow-hidden">
                    {searchResults.map(u => (
                      <button
                        key={u.id}
                        onClick={() => {
                          if (selectedUsers.length >= maxParticipants) return;
                          setSelectedUsers(prev => [...prev, u]);
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        disabled={selectedUsers.length >= maxParticipants}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-kod-purple-700/70 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <div className="w-8 h-8 rounded-full bg-kod-purple-600 flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {(u.username || u.full_name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white truncate">{u.username || u.full_name || 'Unknown'}</p>
                          {u.full_name && u.username && <p className="text-xs text-white/40 truncate">{u.full_name}</p>}
                          {u.email && <p className="text-xs text-white/30 truncate">{u.email}</p>}
                        </div>
                        <span className="ml-auto text-xs text-kod-accent font-medium flex-shrink-0">Add</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedUsers.length > 0 && (
                <div className="bg-kod-purple-700/30 border border-kod-purple-500/40 rounded-lg p-3 space-y-1.5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-white/50 uppercase tracking-wide">
                      Selected — {selectedUsers.length} / {maxParticipants}
                    </span>
                    <div className={`w-full max-w-[120px] h-1.5 rounded-full bg-kod-purple-600 overflow-hidden ml-3`}>
                      <div
                        className="h-full rounded-full bg-kod-accent transition-all"
                        style={{ width: `${(selectedUsers.length / maxParticipants) * 100}%` }}
                      />
                    </div>
                  </div>
                  {selectedUsers.map((u, i) => (
                    <div key={u.id} className="flex items-center gap-2 bg-kod-purple-800/50 rounded-lg px-3 py-2">
                      <span className="text-white/30 text-xs w-5">#{i + 1}</span>
                      <div className="w-6 h-6 rounded-full bg-kod-purple-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(u.username || u.full_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-white truncate flex-1">{u.username || u.full_name}</span>
                      <button
                        onClick={() => setSelectedUsers(prev => prev.filter(s => s.id !== u.id))}
                        className="text-white/30 hover:text-red-400 transition-colors flex-shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {tournamentSongs.length === 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-amber-400 text-sm flex items-center gap-2">
                  <AlertTriangle size={15} />
                  No songs assigned to this tournament. Add songs first.
                </div>
              )}

              <button
                onClick={handleSeedTournament}
                disabled={loading || tournamentSongs.length === 0 || selectedUsers.length < 2}
                className="w-full btn-primary flex items-center justify-center gap-2 py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <Play size={18} />
                )}
                {loading ? 'Seeding…' : `Seed ${selectedUsers.length > 0 ? `${selectedUsers.length} + up to ${maxParticipants - selectedUsers.length} random` : maxParticipants + ' random'} Participant${maxParticipants !== 1 ? 's' : ''}`}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex gap-2 flex-wrap">
                  {Array.from({ length: simState.totalRounds }, (_, i) => i + 1).map(r => (
                    <div
                      key={r}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                        simState.currentRound === r
                          ? 'bg-kod-accent text-white'
                          : roundGroups[r]?.every(m => m.status === 'simulated')
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-kod-purple-700 text-white/50'
                      }`}
                    >
                      {getRoundLabel(r, simState.totalRounds)}
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleReset}
                  disabled={loading}
                  className="btn-outline text-red-400 hover:bg-red-500/10 hover:border-red-500/40 flex items-center gap-2 text-sm"
                >
                  <RotateCcw size={14} />
                  Reset Simulation
                </button>
              </div>

              {Object.entries(roundGroups)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([roundNum, roundMatches]) => {
                  const rn = Number(roundNum);
                  const allDone = roundMatches.every(m => m.status === 'simulated');
                  const prevRoundDone = rn === 1 || (roundGroups[rn - 1] && roundGroups[rn - 1].every(m => m.status === 'simulated'));

                  return (
                    <div key={roundNum} className={`rounded-xl border transition-all ${
                      !prevRoundDone ? 'opacity-40' : 'opacity-100'
                    } ${allDone ? 'border-green-500/30 bg-green-500/5' : 'border-kod-purple-600 bg-kod-purple-700/30'}`}>
                      <div className="px-5 py-3 border-b border-current/10 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {rn === simState.totalRounds ? (
                            <Crown size={16} className="text-yellow-400" />
                          ) : (
                            <Swords size={16} className="text-kod-accent" />
                          )}
                          <span className="font-semibold">{getRoundLabel(rn, simState.totalRounds)}</span>
                          <span className="text-white/40 text-sm">({roundMatches.length} match{roundMatches.length !== 1 ? 'es' : ''})</span>
                        </div>
                        {allDone && <CheckCircle size={16} className="text-green-400" />}
                      </div>

                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {roundMatches.map(match => (
                          <MatchCard
                            key={match.id}
                            match={match}
                            isSimulating={simulatingMatchId === match.id}
                            onSimulate={() => handleSimulateMatch(match)}
                            disabled={!prevRoundDone || simulatingMatchId !== null}
                            songNames={tournamentSongs.map(s => s.song?.title || '').filter(Boolean)}
                            selectedWinnerId={selectedWinners[match.id]}
                            onSelectWinner={(userId) => setSelectedWinners(prev => ({
                              ...prev,
                              [match.id]: prev[match.id] === userId ? '' : userId
                            }))}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}

              {simState.participants.length > 0 && (
                <div className="bg-kod-purple-700/30 rounded-xl border border-kod-purple-600 p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Users size={16} className="text-kod-accent" />
                    Participants
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {simState.participants
                      .sort((a, b) => a.seed - b.seed)
                      .map(p => (
                        <div
                          key={p.userId}
                          className={`flex items-center gap-2 bg-kod-purple-800/50 rounded-lg px-3 py-2 text-sm ${
                            p.eliminated ? 'opacity-40' : ''
                          }`}
                        >
                          <span className="text-white/40 text-xs w-4">#{p.seed}</span>
                          <span className={`font-medium truncate ${getStatusColor(p)}`}>{p.username}</span>
                          {p.eliminated && <X size={12} className="text-red-400 ml-auto flex-shrink-0" />}
                          {!p.eliminated && p.currentRound > 1 && (
                            <ChevronRight size={12} className="text-green-400 ml-auto flex-shrink-0" />
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface MatchCardProps {
  match: SimMatch;
  isSimulating: boolean;
  onSimulate: () => void;
  disabled: boolean;
  songNames: string[];
  selectedWinnerId?: string;
  onSelectWinner?: (userId: string) => void;
}

const MatchCard: React.FC<MatchCardProps> = ({ match, isSimulating, onSimulate, disabled, songNames, selectedWinnerId, onSelectWinner }) => {
  if (match.status === 'pending' && !match.p1 && !match.p2) {
    return (
      <div className="bg-kod-purple-800/40 rounded-lg p-4 border border-kod-purple-600/50 border-dashed">
        <div className="flex items-center gap-2 text-white/30 text-sm">
          <Clock size={14} />
          <span>Waiting for previous round winners</span>
        </div>
      </div>
    );
  }

  if (match.status === 'simulated' && match.winner) {
    return (
      <div className="bg-green-500/5 rounded-lg p-4 border border-green-500/20">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-white/40">Match {match.matchNumber}</span>
          <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle size={12} /> Completed</span>
        </div>
        <ParticipantRow p={match.p1} isWinner={match.winner.userId === match.p1?.userId} score={match.p1?.scores[match.round]} />
        <div className="text-center text-white/20 text-xs my-1">vs</div>
        <ParticipantRow p={match.p2} isWinner={match.winner.userId === match.p2?.userId} score={match.p2?.scores[match.round]} />
      </div>
    );
  }

  const hasSelection = !!selectedWinnerId;

  return (
    <div className="bg-kod-purple-800/60 rounded-lg p-4 border border-kod-purple-500/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-white/40">Match {match.matchNumber}</span>
        <div className="flex items-center gap-2">
          {hasSelection && (
            <span className="text-xs text-yellow-400 flex items-center gap-1">
              <Trophy size={10} /> Winner picked
            </span>
          )}
          <span className="text-xs text-kod-accent">Ready</span>
        </div>
      </div>
      <p className="text-xs text-white/40 mb-2">Click a player to pick the winner (optional)</p>
      <SelectableParticipantRow
        p={match.p1}
        isSelected={selectedWinnerId === match.p1?.userId}
        onSelect={() => match.p1 && onSelectWinner?.(match.p1.userId)}
        disabled={disabled || isSimulating}
      />
      <div className="text-center text-white/30 text-xs my-1">vs</div>
      <SelectableParticipantRow
        p={match.p2}
        isSelected={selectedWinnerId === match.p2?.userId}
        onSelect={() => match.p2 && onSelectWinner?.(match.p2.userId)}
        disabled={disabled || isSimulating}
      />
      <button
        onClick={onSimulate}
        disabled={disabled || isSimulating}
        className="mt-3 w-full bg-kod-accent/20 hover:bg-kod-accent/30 border border-kod-accent/40 text-kod-accent rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {isSimulating ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-kod-accent" />
        ) : (
          <Play size={14} />
        )}
        {isSimulating ? 'Simulating...' : 'Simulate Match'}
      </button>
    </div>
  );
};

interface SelectableParticipantRowProps {
  p?: SimParticipant;
  isSelected: boolean;
  onSelect: () => void;
  disabled: boolean;
}

const SelectableParticipantRow: React.FC<SelectableParticipantRowProps> = ({ p, isSelected, onSelect, disabled }) => {
  if (!p) {
    return (
      <div className="flex items-center gap-2 py-1 px-2">
        <div className="w-6 h-6 rounded-full bg-kod-purple-600/40 flex items-center justify-center">
          <span className="text-white/20 text-xs">?</span>
        </div>
        <span className="text-white/30 text-sm">TBD</span>
      </div>
    );
  }
  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      className={`w-full flex items-center gap-2 py-2 px-2 rounded-lg transition-all text-left ${
        isSelected
          ? 'bg-yellow-500/20 border border-yellow-500/50 ring-1 ring-yellow-500/30'
          : 'bg-kod-purple-700/40 border border-transparent hover:border-white/20 hover:bg-kod-purple-700/70'
      } disabled:cursor-not-allowed`}
    >
      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 bg-kod-purple-600 text-white/70">
        {(p.username || '?').charAt(0).toUpperCase()}
      </div>
      <span className={`text-sm font-medium truncate flex-1 ${p.username ? 'text-white' : 'text-red-400 italic'}`}>
        {p.username || 'Unknown user'}
      </span>
      {isSelected && <Trophy size={12} className="text-yellow-400 flex-shrink-0" />}
    </button>
  );
};

interface ParticipantRowProps {
  p?: SimParticipant;
  isWinner?: boolean;
  score?: number;
}

const ParticipantRow: React.FC<ParticipantRowProps> = ({ p, isWinner, score }) => {
  if (!p) {
    return (
      <div className="flex items-center gap-2 py-1">
        <div className="w-6 h-6 rounded-full bg-kod-purple-600/40 flex items-center justify-center">
          <span className="text-white/20 text-xs">?</span>
        </div>
        <span className="text-white/30 text-sm">TBD</span>
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-2 py-1 rounded px-1 ${isWinner ? 'bg-green-500/10' : isWinner === false ? 'opacity-50' : ''}`}>
      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 bg-kod-purple-600 text-white/70">
        {(p.username || '?').charAt(0).toUpperCase()}
      </div>
      <span className={`text-sm font-medium truncate flex-1 ${p.username ? 'text-white' : 'text-red-400 italic'}`}>
        {p.username || 'Unknown user'}
      </span>
      {isWinner && <Trophy size={12} className="text-yellow-400 flex-shrink-0" />}
      {score !== undefined && (
        <span className={`text-xs font-semibold flex-shrink-0 ${isWinner ? 'text-green-400' : 'text-white/50'}`}>
          {score}
        </span>
      )}
    </div>
  );
};
